/**
 * Farm & Acre Co - State Compliance Handler
 *
 * Shows/hides state-specific compliance sections based on selected state:
 * - SA: Biosecurity levy notice + acknowledgment checkbox
 * - VIC: Warning - must order via Agriculture Victoria website (form disabled)
 * - QLD: DPI consent radio buttons
 *
 * Manages required field states so hidden fields don't block form submission.
 */

(function () {
  'use strict';

  const COMPLIANCE_SECTIONS = {
    SA: 'sa-levy-section',
    VIC: 'vic-notice-section',
    QLD: 'qld-dpi-section',
    TAS: 'tas-pid-section',
  };

  // VIC NLIS tags can't be ordered through our store — send the customer to Agriculture Victoria.
  var VIC_ORDER_URL = 'https://tags.agriculture.vic.gov.au/';

  function isVicSelected() {
    var s = document.getElementById('allflex-state');
    return !!(s && s.value === 'VIC');
  }

  function isProductAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    return action.indexOf('/cart/add') > -1 || !!form.querySelector('button[name="add"], [type="submit"][name="add"]');
  }

  // Belt-and-braces: even though the VIC button is re-pointed, Dawn re-enables the submit button,
  // so we also intercept submit + click at the document (capture) level and redirect instead of
  // adding to cart — but ONLY when VIC is selected on an NLIS product form.
  function initVicRedirectGuards() {
    document.addEventListener('submit', function (e) {
      if (isVicSelected() && isProductAddForm(e.target)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        window.location.href = VIC_ORDER_URL;
      }
    }, true);

    document.addEventListener('click', function (e) {
      if (!isVicSelected()) return;
      var btn = e.target.closest && e.target.closest('button[name="add"], button[type="submit"]');
      if (!btn) return;
      var form = btn.closest('form');
      if (!isProductAddForm(form)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      window.location.href = VIC_ORDER_URL;
    }, true);
  }

  function initStateCompliance() {
    const stateSelect = document.getElementById('allflex-state');
    if (!stateSelect) return;

    const complianceContainer = document.getElementById('AllflexStateCompliance');
    if (!complianceContainer) return;

    // Redirect guards are global + cheap; safe to attach once on any NLIS page.
    initVicRedirectGuards();

    stateSelect.addEventListener('change', function () {
      updateComplianceSections(this.value, complianceContainer);
    });

    // Initialize on page load
    updateComplianceSections(stateSelect.value, complianceContainer);

    // Handle QLD DPI consent type radio buttons
    initQldDpiConsentToggle();
  }

  function initQldDpiConsentToggle() {
    const qldConsentRadios = document.querySelectorAll('[data-qld-consent-type]');
    const qldDpiDetails = document.getElementById('qld-dpi-details');

    if (!qldDpiDetails || qldConsentRadios.length === 0) return;

    qldConsentRadios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        const consentType = this.getAttribute('data-qld-consent-type');

        if (consentType === 'existing') {
          // Show DPI details fields and make them required
          qldDpiDetails.style.display = '';
          qldDpiDetails.querySelectorAll('[data-allflex-field]').forEach(function (field) {
            field.required = true;
          });
        } else {
          // Hide DPI details fields and remove required
          qldDpiDetails.style.display = 'none';
          qldDpiDetails.querySelectorAll('[data-allflex-field]').forEach(function (field) {
            field.required = false;
            field.value = ''; // Clear values when hidden
          });
        }
      });
    });
  }

  function updateComplianceSections(selectedState, container) {
    var showSaLevy = container.dataset.showSaLevy === 'true';

    // Hide all compliance sections and disable their required inputs
    Object.entries(COMPLIANCE_SECTIONS).forEach(function (entry) {
      var state = entry[0];
      var sectionId = entry[1];
      var section = document.getElementById(sectionId);
      if (!section) return;

      // Skip SA levy section if product metafield says not to show it (e.g. sheep products)
      var shouldShow = state === selectedState;
      if (state === 'SA' && !showSaLevy) {
        shouldShow = false;
      }

      if (shouldShow) {
        section.style.display = '';
        enableRequiredFields(section);
      } else {
        section.style.display = 'none';
        disableRequiredFields(section);
        clearFieldValues(section);
      }
    });

    // VIC: disable form submission - orders must go through Agriculture Victoria
    handleVicBlock(selectedState);
  }

  function handleVicBlock(selectedState) {
    var customFields = document.getElementById('AllflexCustomFields');
    if (!customFields) return;

    // Find all fields after the state compliance container
    var complianceContainer = document.getElementById('AllflexStateCompliance');
    if (!complianceContainer) return;

    // Get all sibling fields after the compliance container
    var sibling = complianceContainer.nextElementSibling;
    while (sibling) {
      // State-gated rows (e.g. the Tasmania PID section) own their own visibility via
      // updateComplianceSections — don't let the VIC show/hide blanket-toggle them.
      if (sibling.hasAttribute && sibling.hasAttribute('data-state-section')) {
        sibling = sibling.nextElementSibling;
        continue;
      }
      if (selectedState === 'VIC') {
        sibling.style.display = 'none';
        // Remove required from hidden fields so they don't block other interactions
        sibling.querySelectorAll('[required]').forEach(function (el) {
          el.dataset.wasRequired = 'true';
          el.required = false;
        });
      } else {
        sibling.style.display = '';
        // Restore required attributes
        sibling.querySelectorAll('[data-was-required]').forEach(function (el) {
          el.required = true;
          delete el.dataset.wasRequired;
        });
      }
      sibling = sibling.nextElementSibling;
    }

    // Re-label the add-to-cart button to a clear CTA. We DON'T disable it — Dawn re-enables
    // disabled buttons on its own renders (that's why VIC previously still added to cart). Instead
    // the document-level submit/click guards (initVicRedirectGuards) send VIC orders to Ag Vic.
    var form = customFields.closest('form') || document.querySelector('product-form form, form[action*="/cart/add"]');
    if (form) {
      var submitBtn = form.querySelector('[type="submit"], button[name="add"]');
      if (submitBtn) {
        if (selectedState === 'VIC') {
          if (!submitBtn.dataset.originalText) {
            submitBtn.dataset.originalText = submitBtn.textContent;
          }
          submitBtn.disabled = false;
          submitBtn.setAttribute('aria-disabled', 'false');
          submitBtn.textContent = 'Order via Victoria';
        } else if (submitBtn.dataset.originalText) {
          submitBtn.textContent = submitBtn.dataset.originalText;
          delete submitBtn.dataset.originalText;
        }
      }
    }
  }

  function enableRequiredFields(section) {
    section.querySelectorAll('[data-allflex-field]').forEach(function (field) {
      var fieldName = field.getAttribute('data-allflex-field');
      if (field.type === 'checkbox' || field.type === 'radio') {
        // For checkboxes that were marked as required
        if (fieldName === 'sa-levy') {
          field.required = true;
        }
        if (fieldName === 'qld-dpi') {
          // Only first radio in group needs required
          var firstRadio = section.querySelector('[data-allflex-field="qld-dpi"]');
          if (firstRadio === field) {
            field.required = true;
          }
        }
      } else if (fieldName === 'tas-pid') {
        // Tasmania Party ID (text input) is mandatory only while the TAS section is shown.
        field.required = true;
      }
    });
  }

  function disableRequiredFields(section) {
    section.querySelectorAll('[data-allflex-field]').forEach(function (field) {
      field.required = false;
    });
  }

  function clearFieldValues(section) {
    section.querySelectorAll('input[type="checkbox"]').forEach(function (cb) {
      cb.checked = false;
    });
    section.querySelectorAll('input[type="radio"]').forEach(function (radio) {
      radio.checked = false;
    });
    // Clear the Tasmania PID text input when its section is hidden, so switching away from
    // Tasmania doesn't leave a stale PID on the (now non-TAS) order.
    section.querySelectorAll('input[data-allflex-field="tas-pid"]').forEach(function (input) {
      input.value = '';
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStateCompliance);
  } else {
    initStateCompliance();
  }
})();
